import {
  CloudAccountApiDeleteCloudAccountByUuidRequest,
  CloudAccountApiDiscoverInventoryRequest,
  CloudAccountApiGetCloudAccountByUuidRequest,
  CloudAccountApiUpdateCloudAccountRequest,
  CloudAccountGetResponse,
  CodeRepositoryApiOnboardCodeRepositoryRequest,
} from '@ariksa/cloud-account/api';
import {
  AlertWorkflowApiGetWorkflowsByClientRequest,
  AlertWorkflowResponse,
  ClientConfig,
  ClientResponse,
  ClientsApiAddClientRequest,
  ClientsApiDeleteClientConfigRequest,
  ClientsApiEditClientRequest,
  ClientsApiGetClientByIdRequest,
  ClientsApiGetClientDetailsRequest,
  ClientsApiGetJiraProjectRequest,
  ClientsApiGetJiraProjectsRequest,
  ClientsApiValidateClientConfigRequest,
  JiraProjects,
} from '@ariksa/notification/api';
import { PayloadAction } from '@reduxjs/toolkit';
import { isEmpty } from 'lodash';
import forEach from 'lodash/forEach';
import { QueryAction } from 'services/types';
import { QueryState } from 'services/utils/QueryState';

import {
  IdentityProviderNameServiceListResponse,
  IdentityProviderServiceCreateResponse,
  IdentityProviderServiceListResponse,
} from 'api/auth/api.pb';
import { onApiCall, onApiCallError, onApiCallSuccess } from 'api/call_status';
import {
  apiCallStateInitialState,
  apiCallStatusInitialValue,
} from 'api/initial_values';
import {
  CreateProviderProps,
  DeleteProviderProps,
  IntegrationsTabs,
} from 'containers/Setup/Integrations/types';
import { createSlice } from 'utils/@reduxjs/toolkit';
import { randomString } from 'utils/string';

import { ContainerState } from './types';

// The initial state of the Integrations container
export const initialState: ContainerState = {
  activeClient: QueryState.init({} as any),
  client: QueryState.init({}),
  clients: {
    loading: false,
    error: false,
    slack: [],
    jira: [],
    servicenow: [],
    teams: [],
    email: [],
    sns: [],
    elasticsearch: [],
  },

  client_name: '',
  allClients: QueryState.init([]),
  currentTab: IntegrationsTabs.Saas,
  jiraProjects: QueryState.init({} as JiraProjects),
  jiraProjectDetails: QueryState.init({} as JiraProjects),
  workflows: QueryState.init([]),
  validateClient: QueryState.init({}),

  codeRepositories: QueryState.init([]),

  sso: {
    providers: apiCallStateInitialState([]),
    providerNames: apiCallStateInitialState([]),
    providerForm: {
      formData: {
        providerConfig: {},
      },
      createProvider: apiCallStatusInitialValue,
    },
    deleteProvider: apiCallStatusInitialValue,
  },

  account: QueryState.init({}),
  rediscoverAccount: QueryState.init({}),
};

const integrationsSlice = createSlice({
  name: 'integrations',
  initialState,
  reducers: {
    getAllClients(
      state,
      action: QueryAction<ClientResponse[], ClientsApiGetClientDetailsRequest>,
    ) {
      state.allClients = QueryState.next(state.allClients, action);
    },

    updateAccount(
      state,
      action: QueryAction<any, CloudAccountApiUpdateCloudAccountRequest>,
    ) {
      state.account = QueryState.next(state.account, action);
    },
    deleteAccount(
      state,
      action: QueryAction<any, CloudAccountApiDeleteCloudAccountByUuidRequest>,
    ) {
      state.account = QueryState.next(state.account, action);
    },
    rediscoverInventory(
      state,
      action: QueryAction<any, CloudAccountApiDiscoverInventoryRequest>,
    ) {
      state.rediscoverAccount = QueryState.next(
        state.rediscoverAccount,
        action,
      );
    },

    getAccountByID(
      state,
      action: QueryAction<
        CloudAccountGetResponse,
        CloudAccountApiGetCloudAccountByUuidRequest
      >,
    ) {
      state.account = QueryState.next(state.account, action);
    },

    getProviderNames(state) {
      onApiCall(state.sso.providerNames);
    },
    getProviderNamesSuccess(
      state,
      action: PayloadAction<IdentityProviderNameServiceListResponse>,
    ) {
      state.sso.providerNames.data = action.payload.providers ?? [];
      onApiCallSuccess(state.sso.providerNames);
    },
    getProviderNamesError(state, action: PayloadAction<any>) {
      onApiCallError(state.sso.providerNames, action.payload);
    },
    resetProviderNamesError(state) {
      state.sso.providerNames.error = initialState.sso.providerNames.error;
    },

    getSSOProviders(state) {
      onApiCall(state.sso.providers);
    },
    getSSOProvidersSuccess(
      state,
      action: PayloadAction<IdentityProviderServiceListResponse>,
    ) {
      state.sso.providers.data = action.payload.identity_providers ?? [];
      onApiCallSuccess(state.sso.providers);
    },
    getSSOProvidersError(state, action: PayloadAction<any>) {
      onApiCallError(state.sso.providers, action.payload);
    },

    createSSOProvider(state, action: PayloadAction<CreateProviderProps>) {
      onApiCall(state.sso.providerForm.createProvider);
    },
    createSSOProviderSuccess(
      state,
      action: PayloadAction<IdentityProviderServiceCreateResponse>,
    ) {
      onApiCallSuccess(state.sso.providerForm.createProvider);
    },
    createSSOProviderError(state, action: PayloadAction<any>) {
      onApiCallError(state.sso.providerForm.createProvider, action.payload);
    },
    resetCreateSSOProviderError(state) {
      state.sso.providerForm.createProvider.error =
        initialState.sso.providerForm.createProvider.error;
    },

    deleteSSOProvider(state, action: PayloadAction<DeleteProviderProps>) {
      onApiCall(state.sso.deleteProvider);
    },
    deleteSSOProviderSuccess(state, action: PayloadAction<any>) {
      onApiCallSuccess(state.sso.deleteProvider);
    },
    deleteSSOProviderError(state, action: PayloadAction<any>) {
      onApiCallError(state.sso.deleteProvider, action.payload);
    },

    // Update Client Details
    updateClientById(
      state,
      action: QueryAction<ClientResponse, ClientsApiEditClientRequest>,
    ) {
      state.client = QueryState.next(state.client, action);
    },
    //create client
    createClient(state, action: QueryAction<any, ClientsApiAddClientRequest>) {
      state.client = QueryState.next(state.client, action);
    },
    //delete client
    deleteClient(
      state,
      action: QueryAction<any, ClientsApiDeleteClientConfigRequest>,
    ) {
      state.client = QueryState.next(state.client, action);
    },
    //validate client
    validateClient(
      state,
      action: QueryAction<ClientConfig, ClientsApiValidateClientConfigRequest>,
    ) {
      state.validateClient = QueryState.next(state.validateClient, action);
    },

    getAlertWorkflowAttachedToClient(
      state,
      action: QueryAction<
        AlertWorkflowResponse[],
        AlertWorkflowApiGetWorkflowsByClientRequest
      >,
    ) {
      state.workflows = QueryState.next(state.workflows, action);
    },

    // Get Client Details by ID
    getClientDetailsById(
      state,
      action: QueryAction<ClientResponse, ClientsApiGetClientByIdRequest>,
    ) {
      state.activeClient = QueryState.next(state.activeClient, action);
    },

    //update active client (needed in Slack and jira)
    updateActiveClient(state, action: PayloadAction<any>) {
      state.activeClient.data = action.payload;
    },

    //load projects and users for jira
    getJiraProjects(
      state,
      action: QueryAction<JiraProjects, ClientsApiGetJiraProjectsRequest>,
    ) {
      state.jiraProjects = QueryState.next(state.jiraProjects, action);
    },
    getJiraProjectDetails(
      state,
      action: QueryAction<JiraProjects, ClientsApiGetJiraProjectsRequest>,
    ) {
      state.jiraProjectDetails = QueryState.next(
        state.jiraProjectDetails,
        action,
      );
    },
    getJiraProjectDetailsForAClient(
      state,
      action: QueryAction<JiraProjects, ClientsApiGetJiraProjectRequest>,
    ) {
      state.jiraProjectDetails = QueryState.next(
        state.jiraProjectDetails,
        action,
      );
    },

    resetActiveClient(state) {
      state.activeClient = initialState.activeClient;
    },

    updateCurrentTab(state, action: PayloadAction<any>) {
      state.currentTab = action.payload;
    },

    //get clients
    loadClients(
      state,
      action: PayloadAction<ClientsApiGetClientDetailsRequest>,
    ) {
      const { clientName } = action.payload;
      if (clientName) {
        state.clients[clientName] = [];
        state.client_name = clientName;
      }
      onApiCall(state.clients);
    },
    clientsLoaded(
      state,
      action: PayloadAction<{ client_name: string; data: any }>,
    ) {
      if (action.payload.client_name)
        state.clients[state.client_name] = action.payload.data;
      else {
        state.clients.slack = [];
        state.clients.email = [];
        state.clients.servicenow = [];
        state.clients.teams = [];
        state.clients.jira = [];
        state.clients.sns = [];
        state.clients.elasticsearch = [];
        forEach(action.payload.data, o => state.clients[o.client_name].push(o));
      }
      onApiCallSuccess(state.clients);
    },
    clientsLoadingError(state, action: PayloadAction<any>) {
      state.clients[action.payload.client_name] = [];
      onApiCallError(state.clients, action.payload);
    },
  },
});

export const { actions, reducer, name: sliceKey } = integrationsSlice;
